/**
 * Org Settings → Run budgets (ottotester#211).
 *
 * Owner+admin surface for tuning the three iteration caps the agent
 * pipeline applies per phase. The Run budgets navigation tab is gated
 * behind the `runBudgets.configure` plan feature, so it stays hidden
 * unless an install turns it on (the route still renders on a direct
 * visit; the gate is tab visibility, not a page block).
 *
 * Reads via orgsAPI.getSettings (server get-or-creates the doc, so
 * the form always renders with populated values), writes via
 * updateRunBudgets. Form validates each value is a positive integer
 * before submit; the server validates again.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Button, Input, Label } from '@/components/ui'
import { orgsAPI, DEFAULT_RUN_BUDGETS, type OrgWithRole, type RunBudgets } from '@/lib/api'
import { toUserMessage } from '@/lib/userMessage'
import { useCanI } from '@/hooks/useCanI'

interface DraftBudgets {
  iterationsPerTestCase: string
  iterationsPerScript: string
  iterationsPerHeal: string
}

function toDraft(b: RunBudgets): DraftBudgets {
  return {
    iterationsPerTestCase: String(b.iterationsPerTestCase),
    iterationsPerScript: String(b.iterationsPerScript),
    iterationsPerHeal: String(b.iterationsPerHeal),
  }
}

// Regex match before Number() — `parseInt("1.9")` truncates to 1 and
// `parseInt("1e2")` reads "1", so the admin would silently save a
// budget different from what they typed. Require a clean digit string.
function parseDraft(d: DraftBudgets): { ok: true; value: RunBudgets } | { ok: false; error: string } {
  const fields: Array<keyof DraftBudgets> = [
    'iterationsPerTestCase',
    'iterationsPerScript',
    'iterationsPerHeal',
  ]
  const out: Partial<RunBudgets> = {}
  for (const f of fields) {
    const raw = d[f].trim()
    if (!/^\d+$/.test(raw)) {
      return { ok: false, error: `${f} must be a positive integer` }
    }
    const n = Number(raw)
    if (!Number.isInteger(n) || n <= 0) {
      return { ok: false, error: `${f} must be a positive integer` }
    }
    out[f] = n
  }
  return { ok: true, value: out as RunBudgets }
}

export function OrgRunBudgets({ org }: { org: OrgWithRole }) {
  const canEdit = useCanI('org.writeSettings', org._id)
  const canRead = useCanI('org.readSettings', org._id)
  const [draft, setDraft] = useState<DraftBudgets>(toDraft(DEFAULT_RUN_BUDGETS))
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!canRead) {
      setLoading(false)
      return
    }
    setLoading(true)
    orgsAPI
      .getSettings(org._id)
      .then(s => {
        if (cancelled) return
        setDraft(toDraft(s.runBudgets))
        setLoading(false)
      })
      .catch(err => {
        if (cancelled) return
        setError(
          toUserMessage(
            err,
            "Couldn't load the run budgets. Refresh — if the problem continues, check your connection.",
          ),
        )
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [org._id, canRead])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const parsed = parseDraft(draft)
    if (!parsed.ok) {
      setError(parsed.error)
      return
    }
    setSubmitting(true)
    try {
      const updated = await orgsAPI.updateRunBudgets(org._id, parsed.value)
      setDraft(toDraft(updated.runBudgets))
      setSavedAt(Date.now())
    } catch (err) {
      setError(
        toUserMessage(
          err,
          "Couldn't save the run budgets. Try again — if the problem continues, check your connection.",
        ),
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (!canRead) {
    return (
      <p className="text-sm text-muted-foreground">
        Only owners and admins can view run budgets.
      </p>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-md">
      <div className="space-y-2">
        <Label htmlFor="run-budget-test-case">Planner iterations per test case</Label>
        <Input
          id="run-budget-test-case"
          type="number"
          min={1}
          value={draft.iterationsPerTestCase}
          onChange={e => setDraft(d => ({ ...d, iterationsPerTestCase: e.target.value }))}
          disabled={!canEdit || submitting || loading}
          className="w-32"
        />
        <p className="text-xs text-muted-foreground">
          Total planner budget = this × test cases requested. Default {DEFAULT_RUN_BUDGETS.iterationsPerTestCase}.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="run-budget-script">Generator iterations per script</Label>
        <Input
          id="run-budget-script"
          type="number"
          min={1}
          value={draft.iterationsPerScript}
          onChange={e => setDraft(d => ({ ...d, iterationsPerScript: e.target.value }))}
          disabled={!canEdit || submitting || loading}
          className="w-32"
        />
        <p className="text-xs text-muted-foreground">
          Iteration budget per generated test script. Default {DEFAULT_RUN_BUDGETS.iterationsPerScript}.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="run-budget-heal">Healer iterations per heal attempt</Label>
        <Input
          id="run-budget-heal"
          type="number"
          min={1}
          value={draft.iterationsPerHeal}
          onChange={e => setDraft(d => ({ ...d, iterationsPerHeal: e.target.value }))}
          disabled={!canEdit || submitting || loading}
          className="w-32"
        />
        <p className="text-xs text-muted-foreground">
          Iteration budget per heal attempt. Default {DEFAULT_RUN_BUDGETS.iterationsPerHeal}.
        </p>
      </div>

      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      {savedAt && !error && <p className="text-sm text-success" key={savedAt}>Saved.</p>}

      {canEdit && (
        <div className="flex gap-2">
          <Button type="submit" disabled={submitting || loading}>
            {submitting ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      )}
      {!canEdit && !loading && (
        <p className="text-xs text-muted-foreground">
          Only owners and admins can change run budgets.
        </p>
      )}
    </form>
  )
}
